import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Swal from "sweetalert2";
import FilterModal from "./FilterModal";
import axios from "axios";

import {
  FaSearch,
  FaFilter,
  FaPlus,
  FaMapMarkerAlt,
  FaEllipsisV,
  FaCheck,
  FaTimes,
  FaRegFileAlt,
  FaTruck,
  FaBoxOpen
} from "react-icons/fa";
import {
  FiFileText,
  FiCheckCircle,
  FiClock,
  FiXCircle,
  FiChevronLeft,
  FiChevronRight,
  FiEye,
  FiTruck,
  FiDollarSign,
  FiGrid,
  FiList
} from "react-icons/fi";

const PurchaseOrderList = () => {
  const navigate = useNavigate();
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");

  const [isLoading, setIsLoading] = useState(false);
  const [allOrders, setAllOrders] = useState([]);
  const [displayOrders, setDisplayOrders] = useState([]);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [globalFilter, setGlobalFilter] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [viewMode, setViewMode] = useState("table"); // "table" (list) or "grid"

  const [filterOptions, setFilterOptions] = useState({
    distributor_id: "",
    outlet_id: "",
    status: "",
    method: "",
  });

  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  const fetchAllOrders = async () => {
    setIsLoading(true);
    try {
      if (!token) return;

      const firstRes = await axios.get(`${BASE_URL}/v1/purchasing-order?page=1`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const firstData = firstRes.data;
      if (!firstData.success) return;

      const totalApiPages = firstData.meta?.last_page || 1;
      let completeData = [...(firstData.data || [])];

      for (let page = 2; page <= totalApiPages; page++) {
        try {
          const res = await axios.get(`${BASE_URL}/v1/purchasing-order?page=${page}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.data?.data) {
            completeData = [...completeData, ...res.data.data];
          }
        } catch (e) {
          console.error("Error fetching PO page", page, e);
        }
      }

      const formatted = completeData.map((order) => ({
        raw: order,
        distributor_id: order.distributor?.id ?? null,
        outlet_id: order.outlet?.id ?? null,
        poNumber: order?.po_number ?? order?.number ?? `PO-${order.id}`,
        id: order?.po_number ?? order?.number ?? `PO-${order.id}`,
        supplier: order?.distributor?.name
          ? `${order.distributor.name} (${order.distributor.kota ?? "Pusat"})`
          : "N/A",
        outlet: order?.outlet?.name
          ? `${order.outlet.name} (${order.outlet.kota ?? "Pusat"})`
          : "N/A",
        numericAmount: order?.total_amount || 0,
        amount: order?.total_amount
          ? `Rp ${Number(order.total_amount).toLocaleString("id-ID")}`
          : "Rp 0",
        status: order?.status
          ? order.status.charAt(0).toUpperCase() + order.status.slice(1).toLowerCase()
          : "Pending",
        user: order?.created_by?.fullname || order?.created_by?.name || "Admin/Manager",
        paymentType: order?.method ? order.method.toUpperCase() : "CASH",
        createdDate: order?.created_at
          ? new Date(order.created_at).toLocaleDateString("id-ID", {
              dateStyle: "medium",
            })
          : "-",
      }));

      setAllOrders(formatted);
    } catch (err) {
      console.error("Fetch error PO list:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAllOrders();
  }, []);

  useEffect(() => {
    let filtered = [...allOrders];

    if (selectedStatus !== "All") {
      filtered = filtered.filter(
        (o) => o.status.toLowerCase() === selectedStatus.toLowerCase()
      );
    }

    if (filterOptions.distributor_id) {
      filtered = filtered.filter(
        (o) => o.distributor_id === Number(filterOptions.distributor_id)
      );
    }

    if (filterOptions.outlet_id) {
      filtered = filtered.filter(
        (o) => o.outlet_id === Number(filterOptions.outlet_id)
      );
    }

    if (filterOptions.method) {
      filtered = filtered.filter(
        (o) => o.paymentType.toLowerCase() === filterOptions.method.toLowerCase()
      );
    }

    if (filterOptions.status) {
      filtered = filtered.filter(
        (o) => o.status.toLowerCase() === filterOptions.status.toLowerCase()
      );
    }

    const s = globalFilter.toLowerCase();
    if (s.trim() !== "") {
      filtered = filtered.filter((o) => {
        return (
          o.id.toLowerCase().includes(s) ||
          o.supplier.toLowerCase().includes(s) ||
          o.outlet.toLowerCase().includes(s) ||
          o.amount.toLowerCase().includes(s) ||
          o.user.toLowerCase().includes(s) ||
          o.status.toLowerCase().includes(s)
        );
      });
    }

    const pages = Math.ceil(filtered.length / pageSize);
    setTotalPages(pages || 1);

    const start = (pageIndex - 1) * pageSize;
    const end = start + pageSize;

    setDisplayOrders(filtered.slice(start, end));
  }, [allOrders, globalFilter, selectedStatus, pageIndex, pageSize, filterOptions]);

  useEffect(() => {
    setPageIndex(1);
  }, [globalFilter, selectedStatus, filterOptions]);

  const handleApprove = async (poNumber) => {
    setOpenDropdown(null);
    const result = await Swal.fire({
      title: `Approve PO "${poNumber}"?`,
      text: "Purchase Order akan disetujui untuk pengiriman barang.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#059669",
      cancelButtonColor: "#6B7280",
      confirmButtonText: "Ya, Approve!",
      cancelButtonText: "Batal",
      customClass: { popup: "rounded-3xl" },
    });

    if (!result.isConfirmed) return;

    try {
      const response = await axios.patch(
        `${BASE_URL}/v1/purchasing-order/${encodeURIComponent(poNumber)}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.status === 200) {
        await Swal.fire({
          title: "Berhasil Approved!",
          text: `Surat Pesanan ${poNumber} telah disetujui.`,
          icon: "success",
          timer: 1800,
          showConfirmButton: false,
          customClass: { popup: "rounded-3xl" },
        });

        fetchAllOrders();
      }
    } catch (err) {
      console.error("Approve PO error:", err);
      Swal.fire({
        title: "Gagal Approve",
        text: err.response?.data?.message || "Terjadi kesalahan server.",
        icon: "error",
        customClass: { popup: "rounded-3xl" },
      });
    }
  };

  const handleCancel = async (poNumber) => {
    setOpenDropdown(null);
    const confirm = await Swal.fire({
      title: `Batalkan PO "${poNumber}"?`,
      text: "Status PO akan dibatalkan.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#EF4444",
      cancelButtonColor: "#6B7280",
      confirmButtonText: "Ya, Batalkan!",
      cancelButtonText: "Kembali",
      customClass: { popup: "rounded-3xl" },
    });

    if (!confirm.isConfirmed) return;

    try {
      await axios.delete(
        `${BASE_URL}/v1/purchasing-order/${encodeURIComponent(poNumber)}/delete`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      await Swal.fire({
        title: "Dibatalkan!",
        text: `PO ${poNumber} berhasil dibatalkan.`,
        icon: "success",
        timer: 1800,
        showConfirmButton: false,
        customClass: { popup: "rounded-3xl" },
      });

      fetchAllOrders();
    } catch (err) {
      console.error("Cancel PO error:", err);
      Swal.fire({
        title: "Gagal Membatalkan",
        text: err.response?.data?.message || "Terjadi kesalahan server.",
        icon: "error",
        customClass: { popup: "rounded-3xl" },
      });
    }
  };

  const totalValueSum = allOrders.reduce((sum, o) => sum + (o.numericAmount || 0), 0);
  const pendingCount = allOrders.filter((o) => o.status.toLowerCase() === "pending").length;
  const receivedCount = allOrders.filter((o) => o.status.toLowerCase() === "received").length;

  const getStatusBadge = (status) => {
    const st = (status || "").toLowerCase();
    if (st === "pending") {
      return "bg-amber-50 text-amber-700 border-amber-200";
    } else if (st === "approved") {
      return "bg-teal-50 text-teal-700 border-teal-200";
    } else if (st === "received") {
      return "bg-emerald-50 text-emerald-700 border-emerald-200";
    } else if (st === "canceled") {
      return "bg-rose-50 text-rose-700 border-rose-200";
    } else {
      return "bg-gray-50 text-gray-700 border-gray-200";
    }
  };

  return (
    <div className="space-y-6 pb-12">
      {/* 1. TOP METRIC SUMMARY BAR */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="grid grid-cols-1 sm:grid-cols-4 gap-5"
      >
        <div className="bg-white rounded-3xl p-5 shadow-xl shadow-gray-200/50 border border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">
              Total Purchasing Order
            </p>
            <h2 className="text-3xl font-extrabold text-gray-900">
              {isLoading ? "..." : allOrders.length}
            </h2>
          </div>
          <div className="p-3.5 rounded-2xl bg-emerald-50 text-emerald-600 border border-emerald-100">
            <FiFileText className="w-6 h-6" />
          </div>
        </div>

        <div className="bg-white rounded-3xl p-5 shadow-xl shadow-gray-200/50 border border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">
              Pending Approval
            </p>
            <h2 className="text-3xl font-extrabold text-amber-600">
              {isLoading ? "..." : pendingCount}
            </h2>
          </div>
          <div className="p-3.5 rounded-2xl bg-amber-50 text-amber-600 border border-amber-100">
            <FiClock className="w-6 h-6" />
          </div>
        </div>

        <div className="bg-white rounded-3xl p-5 shadow-xl shadow-gray-200/50 border border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">
              Barang Diterima (Received)
            </p>
            <h2 className="text-3xl font-extrabold text-teal-600">
              {isLoading ? "..." : receivedCount}
            </h2>
          </div>
          <div className="p-3.5 rounded-2xl bg-teal-50 text-teal-600 border border-teal-100">
            <FiCheckCircle className="w-6 h-6" />
          </div>
        </div>

        <div className="bg-white rounded-3xl p-5 shadow-xl shadow-gray-200/50 border border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">
              Total Nominal PO
            </p>
            <h2 className="text-2xl font-extrabold text-emerald-700 truncate max-w-[160px]">
              {isLoading ? "..." : `Rp ${totalValueSum.toLocaleString("id-ID")}`}
            </h2>
          </div>
          <div className="p-3.5 rounded-2xl bg-emerald-50 text-emerald-600 border border-emerald-100">
            <FiDollarSign className="w-6 h-6" />
          </div>
        </div>
      </motion.div>

      {/* 2. MAIN CONTROLS & ACTION BAR */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
        className="bg-white p-6 rounded-3xl shadow-xl shadow-gray-200/50 border border-gray-100 space-y-6"
      >
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          {/* Live Search */}
          <div className="relative w-full md:w-80">
            <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Cari PO number, supplier, outlet..."
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all font-semibold"
            />
          </div>

          <div className="flex flex-wrap items-center gap-3 w-full md:w-auto justify-end">
            {/* View Mode Toggle */}
            <div className="flex items-center p-1 bg-gray-100 rounded-2xl border border-gray-200/80">
              <button
                onClick={() => setViewMode("grid")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                  viewMode === "grid"
                    ? "bg-white text-emerald-700 shadow-sm"
                    : "text-gray-500 hover:text-gray-900"
                }`}
              >
                <FiGrid className="w-3.5 h-3.5" />
                <span>Grid</span>
              </button>
              <button
                onClick={() => setViewMode("table")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                  viewMode === "table"
                    ? "bg-white text-emerald-700 shadow-sm"
                    : "text-gray-500 hover:text-gray-900"
                }`}
              >
                <FiList className="w-3.5 h-3.5" />
                <span>Tabel</span>
              </button>
            </div>

            {/* Filter Modal Trigger */}
            <button
              onClick={() => setIsFilterModalOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 text-xs font-semibold rounded-2xl transition-all shadow-sm"
            >
              <FaFilter className="w-3.5 h-3.5 text-emerald-600" />
              <span>Filter</span>
            </button>

            {/* Create PO CTA (GREEN BUTTON) */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate("/inventory/purchase-order/purchasingorderformadmin")}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-xs font-bold rounded-2xl shadow-lg shadow-emerald-600/20 transition-all"
            >
              <FaPlus className="w-3.5 h-3.5" />
              <span>Buat Surat Pesanan (PO)</span>
            </motion.button>
          </div>
        </div>

        {/* STATUS TABS */}
        <div className="flex items-center gap-2 overflow-x-auto pb-2 text-xs">
          {["All", "Pending", "Approved", "Received", "Canceled"].map((status) => (
            <button
              key={status}
              onClick={() => setSelectedStatus(status)}
              className={`px-4 py-2 rounded-2xl font-bold transition-all whitespace-nowrap ${
                selectedStatus === status
                  ? "bg-emerald-600 text-white shadow-md shadow-emerald-600/20"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {status}
            </button>
          ))}
        </div>

        {/* 3. CONTENT AREA (GRID VS TABLE) */}
        {isLoading ? (
          <div className="flex justify-center items-center py-16">
            <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : displayOrders.length === 0 ? (
          <div className="text-center py-16 text-gray-400 text-sm">
            <FiFileText className="w-12 h-12 mx-auto mb-2 text-gray-300 stroke-1" />
            <p className="font-semibold text-gray-600">Belum ada data Purchasing Order</p>
            <p className="text-xs text-gray-400 mt-1">
              Klik 'Buat Surat Pesanan (PO)' untuk membuat transaksi pembelian obat baru.
            </p>
          </div>
        ) : viewMode === "grid" ? (
          /* GRID CARD VIEW */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {displayOrders.map((order, index) => (
              <motion.div
                key={order.id + index}
                whileHover={{ y: -4 }}
                className="bg-white rounded-3xl p-5 border border-gray-100 shadow-xl shadow-gray-200/40 flex flex-col justify-between space-y-4 hover:border-emerald-200 transition-all duration-300 relative overflow-hidden"
              >
                <div className="space-y-3">
                  <div className="flex justify-between items-start">
                    <div className="space-y-1">
                      <span className="inline-flex px-3 py-1 rounded-full text-xs font-mono font-bold bg-emerald-50 text-emerald-700 border border-emerald-100">
                        {order.id}
                      </span>
                      <h3 className="font-bold text-gray-900 text-base leading-tight pt-1">
                        {order.supplier}
                      </h3>
                    </div>

                    <span
                      className={`inline-flex px-3 py-1 rounded-full text-[11px] font-bold border ${getStatusBadge(order.status)}`}
                    >
                      {order.status}
                    </span>
                  </div>

                  <div className="space-y-2 pt-2 text-xs text-gray-600 border-t border-gray-50">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">Outlet Tujuan</span>
                      <span className="font-semibold text-gray-800">{order.outlet}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">Metode Bayar</span>
                      <span className="font-semibold text-teal-700">{order.paymentType}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">Tanggal Dibuat</span>
                      <span className="text-gray-700 font-semibold">{order.createdDate}</span>
                    </div>
                  </div>

                  <div className="pt-2 border-t border-gray-100 flex items-center justify-between">
                    <span className="text-xs text-gray-400 font-medium">Total Nilai PO</span>
                    <span className="text-lg font-extrabold text-emerald-700">{order.amount}</span>
                  </div>
                </div>

                <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
                  <button
                    onClick={() => navigate(`/inventory/purchase-order/detail/${encodeURIComponent(order.id)}`)}
                    className="flex-1 py-2 px-3 rounded-xl bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-bold transition-all flex items-center justify-center gap-1.5"
                  >
                    <FiEye className="w-3.5 h-3.5" />
                    <span>Lihat Detail</span>
                  </button>

                  {order.status.toLowerCase() === "pending" && (
                    <>
                      <button
                        onClick={() => handleApprove(order.id)}
                        className="p-2 rounded-xl bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-all"
                        title="Approve PO"
                      >
                        <FaCheck className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleCancel(order.id)}
                        className="p-2 rounded-xl bg-rose-50 text-rose-600 hover:bg-rose-100 transition-all"
                        title="Cancel PO"
                      >
                        <FaTimes className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          /* TABLE VIEW */
          <div className="overflow-x-auto rounded-2xl border border-gray-100 shadow-sm">
            <table className="w-full text-left text-xs text-gray-600">
              <thead>
                <tr className="bg-gray-50 text-gray-400 font-bold uppercase tracking-wider text-[10px]">
                  <th className="p-3.5 w-12 text-center">No</th>
                  <th className="p-3.5">PO Number</th>
                  <th className="p-3.5">Supplier (PBF)</th>
                  <th className="p-3.5">Outlet Tujuan</th>
                  <th className="p-3.5">Total Nominal</th>
                  <th className="p-3.5">Metode</th>
                  <th className="p-3.5 text-center">Status</th>
                  <th className="p-3.5 text-center">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {displayOrders.map((order, idx) => (
                  <tr key={order.id + idx} className="hover:bg-emerald-50/20 transition-colors">
                    <td className="p-3.5 text-center font-semibold text-gray-400">
                      {(pageIndex - 1) * pageSize + idx + 1}
                    </td>
                    <td className="p-3.5 font-bold font-mono text-gray-900">{order.id}</td>
                    <td className="p-3.5 font-semibold text-gray-800">{order.supplier}</td>
                    <td className="p-3.5 font-semibold text-gray-700">{order.outlet}</td>
                    <td className="p-3.5 font-extrabold text-emerald-700">{order.amount}</td>
                    <td className="p-3.5 font-bold text-teal-700">{order.paymentType}</td>
                    <td className="p-3.5 text-center">
                      <span
                        className={`inline-flex px-3 py-1 rounded-full text-[11px] font-bold border ${getStatusBadge(order.status)}`}
                      >
                        {order.status}
                      </span>
                    </td>
                    <td className="p-3.5 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => navigate(`/inventory/purchase-order/detail/${encodeURIComponent(order.id)}`)}
                          className="px-3 py-1.5 rounded-xl bg-emerald-50 hover:bg-emerald-100 text-emerald-700 font-bold transition-all flex items-center gap-1.5"
                          title="Lihat Detail PO"
                        >
                          <FiEye className="w-3.5 h-3.5" />
                          <span>Lihat Detail</span>
                        </button>

                        {order.status.toLowerCase() === "pending" && (
                          <>
                            <button
                              onClick={() => handleApprove(order.id)}
                              className="p-2 rounded-xl bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-all"
                              title="Approve PO"
                            >
                              <FaCheck className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleCancel(order.id)}
                              className="p-2 rounded-xl bg-rose-50 text-rose-600 hover:bg-rose-100 transition-all"
                              title="Cancel PO"
                            >
                              <FaTimes className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* PAGINATION CONTROLS */}
        <div className="flex flex-col sm:flex-row items-center justify-between pt-4 border-t border-gray-100 gap-3 text-xs">
          <p className="text-gray-400">
            Menampilkan <span className="font-bold text-gray-700">{displayOrders.length}</span> dari{" "}
            <span className="font-bold text-gray-700">{allOrders.length}</span> PO
          </p>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setPageIndex((p) => Math.max(p - 1, 1))}
              disabled={pageIndex === 1}
              className={`p-2 rounded-xl border flex items-center gap-1 font-semibold transition-all ${
                pageIndex === 1
                  ? "bg-gray-100 text-gray-300 border-gray-200 cursor-not-allowed"
                  : "bg-white text-gray-700 border-gray-200 hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-200"
              }`}
            >
              <FiChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-xs font-semibold text-gray-600 px-2">
              Halaman {pageIndex} dari {totalPages}
            </span>
            <button
              onClick={() => setPageIndex((p) => Math.min(p + 1, totalPages))}
              disabled={pageIndex === totalPages}
              className={`p-2 rounded-xl border flex items-center gap-1 font-semibold transition-all ${
                pageIndex === totalPages
                  ? "bg-gray-100 text-gray-300 border-gray-200 cursor-not-allowed"
                  : "bg-white text-gray-700 border-gray-200 hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-200"
              }`}
            >
              <FiChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </motion.div>

      {/* FILTER MODAL */}
      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        onApply={(f) => {
          setFilterOptions(f);
          setPageIndex(1);
        }}
      />
    </div>
  );
};

export default PurchaseOrderList;
